import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box, ClickAwayListener, Paper, Popper,
    InputAdornment
} from '@mui/material';
import DropdownArrowIcon from './DropdownArrowIcon';
import StyledTextField from 'components/commons/StyledTextField';
import StyledTooltip from 'components/commons/StyledTooltip';

function DropdownSelect({
    label = 'filter',
    value,
    children,
    width,
    WithIcon,
    color = 'primary',
    testId = 'filters-dropdown',
    active = false,
    error = false,
    shrinkLabel = true,
    paddingLess = false,
    fullWidth,
    showValueAsTooltip = false,
}) {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);

    const toggleOpen = () => {
        setOpen((prev) => !prev);
    };

    const handleClickAway = () => {
        setOpen(false);
    };

    const textField = (
        <StyledTextField
            label={label}
            value={error ? '' : (value || 'All')}
            onClick={toggleOpen}
            variant="outlined"
            fullWidth
            isHighlighted={open || active || error}
            error={error}
            focused={open}
            sx={{
                fontSize: '14.857px',
                outline: 'none',
                '& .MuiInputBase-input': {
                    cursor: 'pointer',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                },
            }}
            size="small"
            slotProps={{
                input: {
                    readOnly: true,
                    startAdornment: WithIcon ? (
                        <InputAdornment position="start">
                            <WithIcon
                                style={{ fontSize: 22.286 }}
                                color={open || active ? color : 'initial'}
                                data-testid={`${testId}-dropdown-icon`}
                            />
                        </InputAdornment>
                    ) : null,
                    endAdornment: (
                        <InputAdornment
                            position="start"
                            sx={{marginRight: 0}}
                        >
                            <div data-testid={`${testId}-dropdown-state-identifier`}>
                                <DropdownArrowIcon
                                    isOpen={open} sx={{ fontSize: 22.286 }}
                                />
                            </div>
                        </InputAdornment>
                    ),
                },
                inputLabel: { shrink: shrinkLabel },
            }}
        />
    );

    return (
        <Box
            ref={anchorRef}
            sx={{
                width: width || '100%',
            }}
            data-testid={`dropdown-${label}`}
        >
            {showValueAsTooltip && !!value ? (
                <StyledTooltip title={value} type="light">
                    {textField}
                </StyledTooltip>
            ) : (
                textField
            )}

            <Popper
                open={open}
                anchorEl={anchorRef.current}
                placement="bottom-start"
                sx={{ zIndex: 1300, width: fullWidth ? anchorRef?.current?.offsetWidth : undefined }}
                disablePortal
            >
                <ClickAwayListener onClickAway={handleClickAway}>
                    <Paper
                        elevation={2}
                        sx={{
                            mt: 1,
                            mb: 1,
                            p: paddingLess ? 'initial' : 2,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {
                            React.Children
                                .map(children, child => React.isValidElement(child) ? React.cloneElement(child, { onSelect: handleClickAway }) : child)
                        }
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </Box>
    );
}

DropdownSelect.propTypes = {
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    children: PropTypes.node,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    WithIcon: PropTypes.elementType,
    color: PropTypes.string,
    testId: PropTypes.string,
    active: PropTypes.bool,
    error: PropTypes.bool,
    shrinkLabel: PropTypes.bool,
    paddingLess: PropTypes.bool,
    fullWidth: PropTypes.bool,
    showValueAsTooltip: PropTypes.bool,
};

export default DropdownSelect;